import { profileAxiosInstance as network } from '../../network/axios';
import { useCallback } from 'react';
import { Endpoints, SiteTypes } from '../../network/enums';
import { endpointReplace } from '../../network/utils/endpointReplace';
import { useUpdateBroadcastChannel } from '@mvecom/common/hooks/useUpdateBroadcastChannel';
import { Actions } from '../../actions/enums';
import { AxiosError } from 'axios';
import { getRetailUnitAndLanguage } from '../../network/utils/getRetailUnitAndLanguage';

export type LoginAuthEvent =
  | { status: 'log-in-attempt-pending' }
  | { status: 'log-in-attempt-success' }
  | { status: 'validation-failure' }
  | { status: 'log-in-attempt-failure'; error: AxiosError };

export const useIcapsLogin = (onAuthEvent?: (event: LoginAuthEvent) => void) => {
  const broadcast = useUpdateBroadcastChannel('profile_pages_update', getRetailUnitAndLanguage().retailUnit);

  const login = useCallback(
    (email: string, password: string) => {
      onAuthEvent?.({ status: 'log-in-attempt-pending' });

      return network
        .post(endpointReplace({ ':siteType': SiteTypes.PROFILE }, Endpoints.Auth), {
          password,
          userName: email,
        })
        .then(({ status, data }) => {
          const loggedIn = status >= 200 && status <= 202;

          if (loggedIn) {
            broadcast({
              type: 'profile',
              action: 'signed-in',
            });
            globalThis?.ikea?.pubsub?.publish?.(Actions.USER_LOGGED_IN);
          }

          globalThis?.sendEvent?.({
            custom: {
              user_id: data.userId,
            },
            event_action: 'logins_regular_profile',
            event_category: 'user_actions',
            event_label: loggedIn ? 'success' : 'fail',
            non_interaction: false,
          });

          if (data?.validationFailures) {
            onAuthEvent?.({ status: 'validation-failure' });
            throw new Error('Validation failure');
          } else if (loggedIn) {
            onAuthEvent?.({ status: 'log-in-attempt-success' });
            return data;
          } else {
            const error = new AxiosError('Login failed with status ' + status, status.toString());
            onAuthEvent?.({ status: 'log-in-attempt-failure', error });
            throw error;
          }
        })
        .catch((error) => {
          onAuthEvent?.({ status: 'log-in-attempt-failure', error });
        });
    },
    [broadcast, onAuthEvent],
  );

  return login;
};
